import type {
  CampaignCreationCapabilities,
  SponsoredDisplayCapabilities,
  SponsoredDisplayCreationSubmit,
} from "@adpilot/shared";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CampaignConfigurationSection } from "@/components/campaign-creation/CampaignConfigurationSection";
import { ProductListingPicker } from "@/components/campaign-creation/ProductListingPicker";
import { AdGroupFields } from "@/components/campaign-creation/sponsored-display/AdGroupFields";
import { CampaignFields } from "@/components/campaign-creation/sponsored-display/CampaignFields";
import { CreativeEditor } from "@/components/campaign-creation/sponsored-display/CreativeEditor";
import { buildSponsoredDisplaySubmitPayload } from "@/components/campaign-creation/sponsored-display/submit-payload";
import { TargetingEditor } from "@/components/campaign-creation/sponsored-display/TargetingEditor";
import { useSponsoredDisplayDraft } from "@/components/campaign-creation/sponsored-display/use-sponsored-display-draft";
import { useSponsoredDisplayQueries } from "@/components/campaign-creation/sponsored-display/use-sponsored-display-queries";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";

interface SponsoredDisplayCampaignFormProps {
  shopId: string;
  /** ADP-137: preselect this portfolio when it exists in the shop's list. */
  initialPortfolioId?: string | null;
  requestId: string;
  capabilities: CampaignCreationCapabilities;
  sponsoredDisplayCapabilities: SponsoredDisplayCapabilities;
  isSubmitting: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
  onReconnect: () => void;
  onSubmit: (payload: SponsoredDisplayCreationSubmit) => void;
}

export function SponsoredDisplayCampaignForm({
  shopId,
  initialPortfolioId,
  requestId,
  capabilities,
  sponsoredDisplayCapabilities,
  isSubmitting,
  onDirtyChange,
  onCancel,
  onReconnect,
  onSubmit,
}: SponsoredDisplayCampaignFormProps) {
  const { t } = useTranslation("campaignCreation");
  const {
    draft,
    setDraft,
    update,
    updateCreative,
    addAudienceTarget,
    addContextualTarget,
    applyBidToActiveTargets,
  } = useSponsoredDisplayDraft({ shopId, sponsoredDisplayCapabilities, onDirtyChange });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [invalidProducts, setInvalidProducts] = useState<CampaignCreationInvalidProduct[]>([]);
  const [catalogBlocked, setCatalogBlocked] = useState(true);
  const sdCapabilities = useMemo(
    () => ({
      ...capabilities,
      limits: {
        ...capabilities.limits,
        maxProducts: sponsoredDisplayCapabilities.limits.maxProducts,
      },
    }),
    [capabilities, sponsoredDisplayCapabilities.limits.maxProducts],
  );
  const {
    asins,
    portfolios,
    eligibility,
    contextual,
    audience,
    audienceRecommendationsEnabled,
    assets,
    activeBids,
    ineligible,
    eligibilityWarnings,
    logoAssets,
    imageAssets,
  } = useSponsoredDisplayQueries({
    shopId,
    initialPortfolioId,
    draft,
    setDraft,
    sponsoredDisplayCapabilities,
  });

  function submit() {
    setSubmitAttempted(true);
    const result = buildSponsoredDisplaySubmitPayload(
      {
        draft,
        requestId,
        capabilities,
        sponsoredDisplayCapabilities,
        catalogBlocked,
        eligibilityPending: eligibility.isPending,
        eligibility: eligibility.data,
        hasIneligibleProducts: ineligible.length > 0,
        activeBids,
        logoAssets,
        imageAssets,
      },
      t,
    );
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    onSubmit(result.payload);
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 pb-2" data-slot="sponsored-display-campaign-form">
      <CampaignConfigurationSection title={t("sd.campaign.title")}>
        <div className="px-6 py-4">
          <CampaignFields
            draft={draft}
            portfolios={portfolios.data ?? []}
            currency={capabilities.currencyCode}
            dailyBudgetLimits={capabilities.dailyBudget}
            submitAttempted={submitAttempted}
            onUpdate={update}
          />
        </div>
      </CampaignConfigurationSection>

      <CampaignConfigurationSection title={t("sd.adGroup.title")}>
        <div className="px-6 py-4">
          <AdGroupFields
            draft={draft}
            sponsoredDisplayCapabilities={sponsoredDisplayCapabilities}
            onUpdate={update}
          />
        </div>
        <section className="border-t border-line px-6 py-4">
          <h3 className="text-[13px] font-semibold text-foreground">{t("sd.products.title")}</h3>
          <div className="mt-3 min-w-0">
            <ProductListingPicker
              shopId={shopId}
              marketplaceId={sdCapabilities.marketplaceId}
              selectionLimit={sdCapabilities.limits.maxProducts}
              selected={draft.products}
              onSelectedChange={(products) => update("products", products)}
              enterInput={draft.productEnterInput}
              onEnterInputChange={(value) => update("productEnterInput", value)}
              invalidProducts={invalidProducts}
              onInvalidProductsChange={setInvalidProducts}
              onCatalogBlockingChange={setCatalogBlocked}
              onReconnect={onReconnect}
            />
            {draft.products.length > 0 && eligibility.isPending ? (
              <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner /> {t("sd.products.checkingEligibility")}
              </p>
            ) : eligibility.isError || eligibility.data?.capability.state !== "supported" ? (
              <Alert className="mt-3" variant="warning">
                <IconAlertTriangle />
                <AlertTitle>{t("sd.products.eligibilityUnavailable")}</AlertTitle>
                <AlertDescription>
                  {t("sd.products.eligibilityUnavailableDescription")}
                </AlertDescription>
              </Alert>
            ) : ineligible.length > 0 ? (
              <Alert className="mt-3" variant="error">
                <IconAlertTriangle />
                <AlertTitle>{t("sd.products.ineligible", { count: ineligible.length })}</AlertTitle>
                <AlertDescription>
                  {ineligible.map((product) => product.asin).join(", ")}
                </AlertDescription>
              </Alert>
            ) : eligibilityWarnings.length > 0 ? (
              <Alert className="mt-3" variant="warning">
                <IconAlertTriangle />
                <AlertTitle>{t("sd.products.eligibilityWarning")}</AlertTitle>
                <AlertDescription>{eligibilityWarnings.join(" · ")}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        </section>
      </CampaignConfigurationSection>

      <CampaignConfigurationSection
        title={
          draft.targetingMode === "audience"
            ? t("sd.targeting.customAudiences")
            : t("sd.targeting.contextualTitle")
        }
      >
        <div className="px-6 py-4">
          <TargetingEditor
            draft={draft}
            capabilities={sponsoredDisplayCapabilities}
            audience={audience.data}
            contextual={contextual.data}
            audienceError={audience.isError}
            contextualError={contextual.isError}
            audienceLoading={audienceRecommendationsEnabled && audience.isPending}
            contextualLoading={asins.length > 0 && contextual.isPending}
            activeBids={activeBids}
            currency={capabilities.currencyCode}
            bidLimits={capabilities.bid}
            onUpdate={update}
            onAddAudience={addAudienceTarget}
            onAddContextual={addContextualTarget}
            onApplyBid={applyBidToActiveTargets}
          />
        </div>
      </CampaignConfigurationSection>

      {draft.products.length > 0 ? (
        <CampaignConfigurationSection title={t("sd.creative.title")}>
          <CreativeEditor
            draft={draft.creative}
            products={draft.products}
            capabilities={sponsoredDisplayCapabilities.creative}
            assetsLoading={assets.isPending}
            assetsUnavailable={assets.isError}
            logoAssets={logoAssets}
            imageAssets={imageAssets}
            onUpdate={updateCreative}
          />
        </CampaignConfigurationSection>
      ) : null}

      <div
        data-slot="sd-form-actions"
        className="mt-4 -mx-1 flex items-center justify-center border-t border-line bg-canvas px-6 py-4"
      >
        <div className="flex gap-3">
          <AlertDialog>
            <AlertDialogTrigger
              render={<Button type="button" variant="outline" className="w-56" />}
            >
              {t("actions.cancel")}
            </AlertDialogTrigger>
            <AlertDialogPopup>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("sd.cancel.title")}</AlertDialogTitle>
                <AlertDialogDescription>{t("sd.cancel.description")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogClose render={<Button variant="outline" />}>
                  {t("actions.keepEditing")}
                </AlertDialogClose>
                <AlertDialogClose render={<Button onClick={onCancel} />}>
                  {t("actions.discard")}
                </AlertDialogClose>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
          <Button type="button" className="w-56" disabled={isSubmitting} onClick={submit}>
            {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
            {isSubmitting ? t("sd.actions.saving") : t("sd.actions.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
